/**
 * KbReceipt — the outcome of a Knowledge Base decision, and the reason for it,
 * rendered as ONE thing.
 *
 * `KB-23`. The count line and the sentence explaining it used to be two
 * independent pieces of JSX with two different guards: the count came from
 * `kb.done` (state, survives), the explanation from `kb.lockedIn.length > 0`
 * (derived from `pending`, and a successful submit is exactly the event that
 * empties `pending`). So the explanation unmounted in the same render as the
 * count appeared. Omar ticked one document, read «2 documenti inviati alla
 * Knowledge Base · 16 esclusi», and the only sentence naming the second
 * document had just disappeared.
 *
 * Three surfaces render this outcome — `KbApprovalPanel`, and `IntakeReviewPanel`
 * twice (with and without the commit bar). Handing them a shared component
 * rather than a shared *string* is the point: a fourth surface added later
 * cannot render the number and forget the reason, because there is no way to
 * ask for one without the other. `useKbDecision`'s own header already records
 * what happens when two callers each keep their own copy.
 *
 * Styling stays with the caller — each of the three has its own width, colour
 * and border — so this owns the CONTENT and its pairing, nothing else.
 */
import { sentLockedInNote, type KbDecision, type ReceiptTone } from "../../lib/useKbDecision";

/** Gate B — the receipt's colour, from the same facts as its words. */
export function receiptColor(tone: ReceiptTone): string {
  return tone === "err" ? "var(--c-err)" : tone === "info" ? "var(--c-ink-2)" : "var(--c-ok)";
}

export function KbReceipt({
  decision,
  detailColor = "var(--c-ink-3)",
}: {
  decision: Pick<KbDecision, "done" | "sentLockedIn" | "notSent">;
  /** The second line is secondary text; the caller picks the ink for its ground. */
  detailColor?: string;
}): JSX.Element | null {
  const { done, sentLockedIn, notSent } = decision;
  if (!done) return null;
  const note = sentLockedInNote(sentLockedIn);
  return (
    <>
      <span>{done}</span>
      {/* Gate B — which documents did not go, and why, under the count that
          says how many. Always the error ink, whatever the caller's colour. */}
      {notSent.map((line) => (
        <span
          key={line}
          data-testid="kb-not-sent"
          style={{ display: "block", marginTop: 4, fontSize: 11.5, color: "var(--c-err)" }}
        >
          {line}
        </span>
      ))}
      {note && (
        <span
          style={{
            display: "block",
            marginTop: 4,
            fontSize: 11.5,
            fontWeight: 400,
            color: detailColor,
          }}
        >
          {note}
        </span>
      )}
    </>
  );
}
